/**
 * FinOps Data-Provider Integrations
 * ===================================
 * Customer connects their real-world data sources here:
 *   - ElectricityMaps  → real-time grid carbon intensity (Sub-systems P, W)
 *   - LaunchDarkly     → real feature-flag rollout schedules (Sub-system Q)
 *   - PostHog          → real product-analytics adoption curves (Sub-system Q)
 *   - OpenTelemetry    → real-time per-customer cost (Sub-system R)
 *
 * Until a provider is connected, the corresponding sub-system page shows
 * seeded demo data with a DemoDataBanner — same algorithm, synthetic inputs.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Leaf, ToggleRight, BarChart3, Activity, CheckCircle2, Loader2, Plug, ExternalLink, X,
} from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PROVIDERS = [
  {
    id: 'electricity_maps',
    label: 'ElectricityMaps',
    powers: ['Carbon-Cost', 'Scope-3'],
    Icon: Leaf,
    color: 'green',
    description: 'Real-time grid carbon intensity (kgCO2/kWh) per region. Auto-refresh every 15min. Powers Carbon-Cost Pareto Optimizer and Scope-3 Carbon Extender.',
    fields: [{ key: 'api_key', label: 'API key', placeholder: 'em_…' }],
    docs: 'https://api.electricitymap.org/',
  },
  {
    id: 'launchdarkly',
    label: 'LaunchDarkly',
    powers: ['Forward Predictor'],
    Icon: ToggleRight,
    color: 'fuchsia',
    description: 'Real feature-flag rollout schedules across all your projects. Powers Forward Cost Predictor signal #3 (feature-flag schedules).',
    fields: [{ key: 'api_key', label: 'API key', placeholder: 'api-…' }],
    docs: 'https://app.launchdarkly.com/settings/authorization',
  },
  {
    id: 'posthog',
    label: 'PostHog',
    powers: ['Forward Predictor'],
    Icon: BarChart3,
    color: 'amber',
    description: 'Product-analytics adoption curves per feature. Powers Forward Cost Predictor signal #4 (real adoption curve).',
    fields: [
      { key: 'host', label: 'Host', placeholder: 'https://app.posthog.com' },
      { key: 'api_key', label: 'Personal API key', placeholder: 'phx_…' },
    ],
    docs: 'https://posthog.com/docs/api',
  },
  {
    id: 'opentelemetry',
    label: 'OpenTelemetry',
    powers: ['Real-Time Unit Econ'],
    Icon: Activity,
    color: 'cyan',
    description: 'Stream trace spans to CARTIE for real-time per-customer cost. We expose an OTLP/HTTP ingest endpoint compatible with the OpenTelemetry Collector.',
    fields: [{ key: 'otlp_endpoint', label: 'Your OTLP endpoint (informational)', placeholder: 'https://otel.your-co.com:4318' }],
    docs: 'https://opentelemetry.io/docs/specs/otlp/',
  },
];

const COLOR_CLASS = {
  green:   { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', accent: 'text-emerald-300', btn: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' },
  fuchsia: { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', accent: 'text-emerald-300', btn: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' },
  amber:   { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', accent: 'text-emerald-300', btn: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' },
  cyan:    { border: 'border-emerald-500/40', bg: 'bg-emerald-500/5', accent: 'text-emerald-300', btn: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' },
};

const ProviderCard = ({ provider, status, onConnected, onDisconnected }) => {
  const color = COLOR_CLASS[provider.color] || COLOR_CLASS.cyan;
  const { Icon } = provider;
  const [formState, setFormState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const live = status?.live === true;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/finops-integrations/${provider.id.replace('_', '-')}/connect`, formState, { headers: HEADERS() });
      toast.success(`${provider.label} connected ✓`);
      onConnected(provider.id, r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || `${provider.label} failed to validate`);
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/finops-integrations/${provider.id}`, { headers: HEADERS() });
      toast.success(`${provider.label} disconnected`);
      onDisconnected(provider.id);
    } catch (e) {
      toast.error('Disconnect failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={`bg-white dark:bg-slate-900/60 border-slate-200 dark:${color.border}`} data-testid={`fi-card-${provider.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon className={`w-6 h-6 text-emerald-600 dark:text-emerald-300`} />
            <div>
              <CardTitle className="text-slate-900 dark:text-white text-lg">{provider.label}</CardTitle>
              <div className="flex items-center gap-1.5 mt-1">
                {provider.powers.map((p) => (
                  <Badge key={p} variant="outline" className="text-emerald-700 dark:text-emerald-300 border-emerald-500/40 bg-emerald-50 dark:bg-transparent text-[9px] font-mono">{p}</Badge>
                ))}
              </div>
            </div>
          </div>
          {live ? (
            <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40" data-testid={`fi-live-${provider.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> LIVE
            </Badge>
          ) : (
            <Badge variant="outline" className="border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-400 text-[10px]" data-testid={`fi-demo-${provider.id}`}>
              demo data
            </Badge>
          )}
        </div>
        <CardDescription className="mt-3 text-slate-600 dark:text-slate-400">{provider.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {live ? (
          <div className="bg-emerald-50 dark:bg-emerald-500/5 rounded-md p-3 text-xs space-y-1.5">
            <div className="text-slate-700 dark:text-slate-300">
              <span className="text-slate-500">Connected by:</span> {status?.connected_by_email || '—'}
            </div>
            <div className="text-slate-700 dark:text-slate-300">
              <span className="text-slate-500">When:</span> {status?.connected_at ? new Date(status.connected_at).toLocaleString() : '—'}
            </div>
            {status?.verification && (
              <div className="text-slate-600 dark:text-slate-400 text-[10px] font-mono pt-1.5 border-t border-slate-200 dark:border-white/5">
                {JSON.stringify(status.verification).slice(0, 100)}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={disconnect} disabled={submitting} className="text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-500/10 mt-2 -ml-2" data-testid={`fi-disconnect-${provider.id}`}>
              <X className="w-3 h-3 mr-1" /> Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {provider.fields.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`${provider.id}-${f.key}`} className="text-xs text-slate-700 dark:text-slate-400">{f.label}</Label>
                <Input
                  id={`${provider.id}-${f.key}`}
                  type={f.key.includes('key') ? 'password' : 'text'}
                  placeholder={f.placeholder}
                  value={formState[f.key] || ''}
                  onChange={(e) => setFormState({ ...formState, [f.key]: e.target.value })}
                  className="bg-white dark:bg-slate-950 border-slate-300 dark:border-white/10 text-slate-900 dark:text-white font-mono text-xs mt-1"
                  data-testid={`fi-input-${provider.id}-${f.key}`}
                />
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button onClick={submit} disabled={submitting || provider.fields.some(f => !formState[f.key])} className={`${color.btn} font-semibold`} data-testid={`fi-connect-${provider.id}`}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plug className="w-4 h-4 mr-1.5" />}
                Connect &amp; validate
              </Button>
              <a href={provider.docs} target="_blank" rel="noreferrer" className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1">
                Get key <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FinOpsIntegrations = () => {
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/finops-integrations/status`, { headers: HEADERS() });
      setStatus(r.data.providers || {});
    } catch (e) {
      // not logged in or no tenant
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  return (
    <div className="bg-transparent text-slate-900 dark:text-slate-100" data-testid="finops-integrations-page">
      <Helmet><title>Data Provider Integrations | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-50 dark:bg-transparent">
            <Plug className="w-3 h-3" /> Real-data mode for Smart Engines
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight leading-[1.05]"><BrandTitle>FinOps Data Providers</BrandTitle></h1>
          <p className="mt-4 text-base text-slate-700 dark:text-slate-300 leading-relaxed">
            Connect your real-world data sources to switch Smart Engines from <span className="text-amber-700 dark:text-amber-200 font-medium">demo</span> to <span className="text-emerald-700 dark:text-emerald-300 font-semibold">LIVE</span>.
          </p>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            All API keys are validated on connect (we reject invalid ones immediately) and stored encrypted at rest. Disconnect any time — the engine falls back gracefully to demo data.
          </p>
        </div>

        {loading ? (
          <Loader2 className="w-8 h-8 animate-spin mx-auto my-16 text-slate-500" />
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fi-grid">
            {PROVIDERS.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                status={status[p.id]}
                onConnected={(id, data) => setStatus({ ...status, [id]: { live: true, ...data } })}
                onDisconnected={(id) => setStatus({ ...status, [id]: null })}
              />
            ))}
          </div>
        )}

        <Card className="mt-10 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-500/10 dark:to-cyan-500/5 border-emerald-500/40">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold mb-2">Always-on integrations</div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">No setup required:</h3>
            <ul className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5">
              <li>• <strong className="text-emerald-700 dark:text-emerald-300">ECB / exchangerate.host</strong> daily FX rates → Multi-Currency P&amp;L</li>
              <li>• <strong className="text-emerald-700 dark:text-emerald-300">Seeded Cloud Carbon Footprint factors</strong> → Carbon-Cost fallback when ElectricityMaps not connected</li>
              <li>• <strong className="text-emerald-700 dark:text-emerald-300">Public CO2 grid factors</strong> → Scope-3 fallback for embodied carbon</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinOpsIntegrations;
